import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { Box, IconButton, Tooltip, Chip } from '@mui/material';

import AddIcon from '@mui/icons-material/Add';

import VisibilityIcon from '@mui/icons-material/Visibility';

import EditIcon from '@mui/icons-material/Edit';

import CheckIcon from '@mui/icons-material/Check';

import SendIcon from '@mui/icons-material/Send';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import CancelIcon from '@mui/icons-material/Cancel';

import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';

import DownloadIcon from '@mui/icons-material/Download';

import { useHistory, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';

import InvDataGrid from '../../components/InvDataGrid';

import StatusChip from '../../components/StatusChip';

import InvButton from '../../components/InvButton';

import ConfirmDialog from '../../components/ConfirmDialog';

import FilterToolbar from '../../components/FilterToolbar';

import { getVendors, fromApiVendor, isVendorActive } from '../../api/vendor';

import useVendorWorkflow from '../../hooks/useVendorWorkflow';

import { VENDOR_TYPES, VENDOR_APPROVAL_STATUSES } from '../../constants';

import { ACTION_CONFIG, isPurchasableVendor } from '../../utils/vendorWorkflow';
import { isPendingApproval, vendorSubmitBlockReason } from '../../utils/vendorEntity';

import { exportToCsv } from '../../utils';

import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS } from '../../theme';




export default function VendorList() {

  const { can } = useInvRole();

  const { url } = useRouteMatch();

  const history = useHistory();

  const [vendors, setVendors] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');

  const [statusFilter, setStatusFilter] = useState('');

  const [activeFilter, setActiveFilter] = useState('');

  const [typeFilter, setTypeFilter] = useState('');

  const [poReadyOnly, setPoReadyOnly] = useState(false);

  const [actionTarget, setActionTarget] = useState(null);

  const [reason, setReason] = useState('');



  const load = useCallback(async () => {

    setLoading(true);

    setError(null);

    try {

      const p = {};

      if (search) p.search = search;

      if (statusFilter) p.approval_status = statusFilter;

      if (typeFilter) p.vendor_type = typeFilter;

      if (poReadyOnly) {

        p.approval_status = 'APPROVED';

        p.is_active = true;

      }

      const data = await getVendors(p);

      const list = Array.isArray(data) ? data : (data?.items || []);

      setVendors(list.map((v) => ({ ...v, ...fromApiVendor(v) })));

    } catch (e) {

      setError(e.message);

    } finally {

      setLoading(false);

    }

  }, [search, statusFilter, typeFilter, poReadyOnly]);



  useEffect(() => { load(); }, [load]);



  const { executeAction, loading: actionLoading } = useVendorWorkflow(

    actionTarget?.vendorId,

    {

      vendor: actionTarget?.vendor,

      onSuccess: () => {

        setActionTarget(null);

        setReason('');

        load();

      },

    },

  );



  async function confirmListAction() {

    if (!actionTarget) return;

    try {

      await executeAction(actionTarget.actionKey, { reason: reason.trim() });

    } catch {

      setActionTarget(null);

      setReason('');

    }

  }



  function openAction(row, actionKey) {

    if (actionKey === 'submit') {
      const msg = vendorSubmitBlockReason(row);
      if (msg) {
        toast.error(msg);
        history.push(`${url}/${row.vendor_id || row.id}/edit`);
        return;
      }
    }

    const config = ACTION_CONFIG[actionKey];

    if (!config) return;

    setActionTarget({

      vendorId: row.vendor_id || row.id,

      vendor: row,

      actionKey,

      title: config.title,

      description: config.description,

      destructive: config.destructive,

      requireReason: config.requireReason,

    });

  }



  const statusOptions = [

    { value: '', label: 'All Approval' },

    ...VENDOR_APPROVAL_STATUSES.map(s => ({ value: s, label: s.replace(/_/g, ' ') })),

  ];



  const activeOptions = [

    { value: '', label: 'All Active' },

    { value: 'active', label: 'Active Only' },

    { value: 'inactive', label: 'Inactive Only' },

  ];



  const filteredVendors = useMemo(() => {

    let list = vendors;

    if (activeFilter === 'active') list = list.filter(isVendorActive);

    if (activeFilter === 'inactive') list = list.filter((v) => !isVendorActive(v));

    if (poReadyOnly) list = list.filter(isPurchasableVendor);

    return list;

  }, [vendors, activeFilter, poReadyOnly]);

  const hasActiveFilters = Boolean(
    search || statusFilter || activeFilter || typeFilter || poReadyOnly,
  );

  function handleResetFilters() {
    setSearch('');
    setStatusFilter('');
    setActiveFilter('');
    setTypeFilter('');
    setPoReadyOnly(false);
  }

  const columns = [

    { field: 'vendor_code', headerName: 'Code', width: 110 },

    { field: 'vendor_name', headerName: 'Vendor Name', flex: 1, minWidth: 160 },

    { field: 'vendor_type', headerName: 'Type', width: 130 },

    { field: 'city', headerName: 'City', width: 110 },

    { field: 'gstin', headerName: 'GSTIN', width: 150 },

    { field: 'approval_status', headerName: 'Approval', width: 130,

      renderCell: (p) => <StatusChip status={p.value} /> },

    { field: 'can_purchase', headerName: 'PO', width: 56, align: 'center', headerAlign: 'center',

      renderCell: (p) => (

        <Tooltip title={isPurchasableVendor(p.row) ? 'Eligible for purchase orders' : 'Not eligible for PO'}>

          {isPurchasableVendor(p.row)

            ? <ShoppingCartCheckoutIcon sx={{ color: IPD_COLORS.success.text, fontSize: 18 }} />

            : <CancelIcon sx={{ color: IPD_COLORS.textSecondary, fontSize: 18, opacity: 0.5 }} />}

        </Tooltip>

      ) },

    { field: 'is_active', headerName: 'Active', width: 72, align: 'center', headerAlign: 'center',

      renderCell: (p) => (

        <Tooltip title={isVendorActive(p.row) ? 'Active' : 'Inactive'}>

          {isVendorActive(p.row)

            ? <CheckCircleOutlineIcon sx={{ color: IPD_COLORS.success.text, fontSize: 18 }} />

            : <CancelIcon sx={{ color: IPD_COLORS.error.text, fontSize: 18 }} />}

        </Tooltip>

      ) },

    { field: 'actions', headerName: '', width: 168, sortable: false,

      renderCell: (p) => {

        const row = p.row;

        const status = row.approval_status;

        const active = isVendorActive(row);

        return (

          <Box sx={{ display: 'flex', gap: 0.5 }}>

            <Tooltip title="View">

              <IconButton size="small" onClick={() => history.push(`${url}/${row.vendor_id || row.id}`)}

                sx={{ color: IPD_COLORS.primary }}><VisibilityIcon fontSize="small" /></IconButton>

            </Tooltip>

            {can(INV_ACTIONS.VENDOR_EDIT) && (

              <Tooltip title="Edit">

                <IconButton size="small"

                  onClick={() => history.push(`${url}/${row.vendor_id || row.id}/edit`)}

                  sx={{ color: IPD_COLORS.primary }}><EditIcon fontSize="small" /></IconButton>

              </Tooltip>

            )}

            {status === 'DRAFT' && active && can(INV_ACTIONS.VENDOR_SUBMIT) && (

              <Tooltip title="Submit for Approval">

                <IconButton size="small" onClick={() => openAction(row, 'submit')}

                  sx={{ color: IPD_COLORS.info.text }}><SendIcon fontSize="small" /></IconButton>

              </Tooltip>

            )}

            {isPendingApproval(status) && active && can(INV_ACTIONS.VENDOR_APPROVE) && (

              <Tooltip title="Approve">

                <IconButton size="small" onClick={() => openAction(row, 'approve')}

                  sx={{ color: IPD_COLORS.success.text }}><CheckIcon fontSize="small" /></IconButton>

              </Tooltip>

            )}

          </Box>

        );

      } },

  ];



  return (

    <Box>

      <PageHeader eyebrow="Material Management" title="Vendor Master"

        description={`${filteredVendors.length} vendors`}

        actions={

          <>

            <InvButton startIcon={<DownloadIcon />} variant="outlined" tone="teal" size="small"

              onClick={() => exportToCsv('vendors.csv', filteredVendors)}>Export</InvButton>

            {can(INV_ACTIONS.VENDOR_CREATE) && (

              <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${url}/new`)}>New Vendor</InvButton>

            )}

          </>

        }

      />






      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="Search vendors..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <FilterToolbar.Filter
          label="Approval"
          width="wide"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          options={statusOptions}
          disabled={poReadyOnly}
        />
        <FilterToolbar.Filter
          label="Active"
          value={activeFilter}
          onChange={e => setActiveFilter(e.target.value)}
          options={activeOptions}
        />
        <FilterToolbar.Filter
          label="Type"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          options={[{ value: '', label: 'All Types' }, ...VENDOR_TYPES]}
        />
        <FilterToolbar.Reset onClick={handleResetFilters} disabled={!hasActiveFilters} />
        <FilterToolbar.Slot>
          <Chip
            label="PO Ready"
            clickable
            onClick={() => setPoReadyOnly(v => !v)}
            sx={{
              fontWeight: 700,
              fontSize: 12,
              height: 40,
              borderRadius: '10px',
              backgroundColor: poReadyOnly ? IPD_COLORS.success.bg : IPD_COLORS.cardBg,
              color: poReadyOnly ? IPD_COLORS.success.text : IPD_COLORS.textSecondary,
              border: `1px solid ${poReadyOnly ? IPD_COLORS.success.border : IPD_COLORS.border}`,
            }}
          />
        </FilterToolbar.Slot>
      </FilterToolbar>



      <InvDataGrid fillHeight rows={filteredVendors} columns={columns} loading={loading} error={error} onRetry={load}

        getRowId={r => r.vendor_id || r.id}

        emptyMessage={poReadyOnly ? 'No PO-ready vendors. Approve vendors to use them in purchase orders.' : 'No vendors found.'}

        emptyAction={can(INV_ACTIONS.VENDOR_CREATE)

          ? <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${url}/new`)}>Add Vendor</InvButton>

          : null} />



      <ConfirmDialog

        open={Boolean(actionTarget)}

        title={actionTarget?.title}

        description={actionTarget?.description}

        destructive={actionTarget?.destructive}

        requireReason={actionTarget?.requireReason}

        reasonValue={reason}

        onReasonChange={setReason}

        loading={actionLoading}

        onConfirm={confirmListAction}

        onCancel={() => { setActionTarget(null); setReason(''); }}

      />

    </Box>

  );

}


